import { createFileRoute } from "@tanstack/react-router";
import { ArticlePage } from "@/components/ArticlePage";
import { articleSchemas } from "@/lib/seo";

const SITE = "https://shopify-freelancer.net";
const PATH = "/shopify-e-commerce-loesung";
const TITLE = "Shopify als E-Commerce-Lösung: Eignung, Kosten, Grenzen";
const DESCRIPTION =
  "Wofür sich Shopify als E-Commerce-Lösung eignet, welche laufenden Kosten anfallen, wo die Grenzen liegen und wann ein Wechsel sinnvoll ist.";

const faqs = [
  {
    q: "Für wen eignet sich Shopify?",
    a: "Für Händler, die verkaufen und nicht Technik betreiben wollen. Hosting, Updates, Sicherheit und Kaufabschluss kommen von der Plattform, Sie kümmern sich um Sortiment, Inhalte und Marketing.",
  },
  {
    q: "Was kostet Shopify laufend?",
    a: "Die Plattformgebühr richtet sich nach dem gewählten Tarif, dazu kommen Zahlungsgebühren und die Kosten der eingesetzten Apps. Die tatsächliche monatliche Summe hängt stark davon ab, wie viele Apps im Einsatz sind.",
  },
  {
    q: "Wo liegen die Grenzen?",
    a: "Der Kaufabschluss lässt sich nur in den höheren Tarifen tiefer anpassen, und sehr spezielle Prozesse landen schnell bei Apps oder eigener Entwicklung. Bei ungewöhnlicher Preis- oder Bestelllogik lohnt eine Prüfung vor der Entscheidung.",
  },
  {
    q: "Shopify oder WooCommerce?",
    a: "Shopify nimmt Ihnen Betrieb und Wartung ab und kostet dafür laufend Gebühren. WooCommerce ist offener und günstiger in der Lizenz, verlagert aber Hosting, Updates und Sicherheit zu Ihnen.",
  },
  {
    q: "Lässt sich ein bestehender Shop migrieren?",
    a: "Produkte, Kunden und Bestellungen lassen sich in der Regel übernehmen. Kritisch sind Weiterleitungen der alten Adressen, damit vorhandene Rankings nicht verloren gehen.",
  },
];

export const Route = createFileRoute("/shopify-e-commerce-loesung")({
  component: Page,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}${PATH}` },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
    scripts: articleSchemas({ title: TITLE, description: DESCRIPTION, path: PATH, faqs }),
  }),
});

function Page() {
  return (
    <ArticlePage
      kicker="Plattform · Entscheidung"
      title="Shopify als E-Commerce-Lösung: was die Plattform abnimmt"
      intro="Die Frage ist selten, ob Shopify verkaufen kann. Die Frage ist, was Sie an Betrieb abgeben wollen, was das laufend kostet und wo Sie mit eigener Logik an Grenzen stoßen."
      source="shopify-e-commerce-loesung"
      sections={[
        {
          h: "Was die Plattform übernimmt",
          p: [
            "Hosting, Updates, Sicherheit, Zahlungsanbindung und ein erprobter Kaufabschluss kommen mit. Das ist der eigentliche Grund, warum viele Händler wechseln: weniger Technik im Alltag.",
          ],
        },
        {
          h: "Womit Sie rechnen sollten",
          p: [],
          list: [
            "Plattformgebühr je nach Tarif",
            "Zahlungsgebühren pro Bestellung",
            "Apps, die sich monatlich summieren",
            "Einmalig: Aufbau, Theme, Datenübernahme",
          ],
        },
        {
          h: "Wo es eng wird",
          p: [
            "Tiefe Eingriffe in den Kaufabschluss sind nur in den höheren Tarifen möglich. Ungewöhnliche Preis-, Mengen- oder B2B-Logik lässt sich abbilden, kostet aber Entwicklungszeit oder eine App.",
            "Vor der Entscheidung lohnt es sich, genau diese Sonderfälle zu prüfen, statt sie später nachzurüsten.",
          ],
        },
        {
          h: "Vergleich in einem Satz",
          p: [
            "Shopify kostet laufend Gebühren und spart Betrieb. Selbst gehostete Systeme sparen Gebühren und kosten Betrieb. Welche Seite günstiger ist, entscheidet Ihre Zeit, nicht die Lizenz.",
          ],
        },
        {
          h: "Beim Wechsel wichtig",
          p: [
            "Produkte, Kunden und Bestellungen lassen sich übernehmen. Entscheidend sind Weiterleitungen der alten Adressen, sonst verlieren Sie Sichtbarkeit, die Sie sich über Jahre erarbeitet haben.",
          ],
        },
      ]}
      faqs={faqs}
      related={[
        { to: "/ratgeber/shopify-vs-woocommerce", label: "Shopify vs. WooCommerce" },
        { to: "/ratgeber/shopify-migration", label: "Migration zu Shopify" },
        { to: "/shopify-shop-erstellen-lassen", label: "Shop erstellen lassen" },
      ]}
    />
  );
}
